import React, { useEffect, useState } from "react";
import { Plus, GraduationCap, Trash2, FileCheck2, PencilLine, Download, Eye } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { PageHead, Loader, EmptyState } from "../components/PageHead";
import { Modal } from "../components/Modal";
import { Badge } from "../components/Badges";
import api, { apiErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

export const SOP_CATEGORIES = ["Overview", "Receiving", "Technical", "Dispatch", "General"];

export default function SopLibrary() {
  const { can } = useAuth();
  const canEdit = can("sops", "edit");
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/sops");
      setRows(r.data);
    } catch (e) { toast.error(apiErr(e)); setRows([]); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (sop) => {
    if (!window.confirm(`Delete "${sop.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/sops/${sop.id}`);
      toast.success("SOP deleted");
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const download = async (sop) => {
    try {
      const r = await api.get(`/sops/${sop.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const grouped = SOP_CATEGORIES.map((cat) => ({
    cat, items: (rows || []).filter((r) => r.category === cat),
  })).filter((g) => g.items.length > 0);
  const uncategorised = (rows || []).filter((r) => !SOP_CATEGORIES.includes(r.category));
  if (uncategorised.length) grouped.push({ cat: "Other", items: uncategorised });

  return (
    <>
      <PageHead crumb="Records" title="SOP / Training Library"
        sub="Standard Operating Procedures for the team — view online or download a letterhead PDF to distribute and print."
        actions={canEdit && <button className="btn gold" onClick={() => setCreating(true)} data-testid="sop-new-btn"><Plus size={15} /> New SOP</button>} />

      {rows === null && <Loader />}
      {rows && rows.length === 0 && (
        <EmptyState icon={GraduationCap}>
          No SOPs yet. {canEdit ? "Click 'New SOP' to author the first one." : "Ask a manager/admin to publish one."}
        </EmptyState>
      )}

      {grouped.map((g) => (
        <div key={g.cat} style={{ marginBottom: 26 }}>
          <div className="t-strong" style={{ fontSize: 13, textTransform: "uppercase", letterSpacing: ".06em", color: "var(--gold-deep)", marginBottom: 10 }}>
            {g.cat}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 14 }}>
            {g.items.map((s) => (
              <div key={s.id} className="card pad" data-testid={`sop-card-${s.id}`}
                style={{ display: "flex", flexDirection: "column", gap: 8, cursor: "pointer" }}
                onClick={() => navigate(`/app/sops/${s.id}`)}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                  <span className="t-strong" style={{ fontSize: 15, display: "inline-flex", alignItems: "center", gap: 6 }}>
                    <GraduationCap size={15} /> {s.title}
                  </span>
                  {s.status === "published" ? <Badge label="Published" cls="b-green" /> : <Badge label="Draft" cls="b-amber" />}
                </div>
                {s.required_reading && (
                  <Badge label="Required reading" cls="b-red" testid={`sop-required-${s.id}`} />
                )}
                <div className="s t-mut">Version {s.version || "1.0"} · {s.steps?.length || 0} step{(s.steps?.length || 0) === 1 ? "" : "s"}</div>
                <div className="s t-mut">
                  {s.updated_by_name ? `Last updated by ${s.updated_by_name}` : ""}
                </div>
                <div style={{ display: "flex", gap: 6, marginTop: "auto", paddingTop: 6 }} onClick={(e) => e.stopPropagation()}>
                  <button className="btn sm ghost" onClick={() => navigate(`/app/sops/${s.id}`)} data-testid={`sop-view-${s.id}`}><Eye size={12} /> View</button>
                  <button className="btn sm ghost" onClick={() => download(s)} data-testid={`sop-dl-${s.id}`}><Download size={12} /> PDF</button>
                  {canEdit && (
                    <button className="btn sm ghost" style={{ marginLeft: "auto", color: "var(--red)" }} onClick={() => remove(s)} data-testid={`sop-del-${s.id}`}>
                      <Trash2 size={12} />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      {creating && <NewSopModal onClose={() => setCreating(false)} onCreated={(id) => navigate(`/app/sops/${id}?edit=1`)} />}
    </>
  );
}

function NewSopModal({ onClose, onCreated }) {
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState(SOP_CATEGORIES[0]);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!title.trim()) { toast.error("Title is required"); return; }
    setBusy(true);
    try {
      const r = await api.post("/sops", { title: title.trim(), category, version: "1.0" });
      toast.success("SOP created — start adding steps");
      onCreated(r.data.id);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <Modal open onClose={onClose} icon={PencilLine} title="New SOP"
      subtitle="Give it a title and category — you'll add Purpose/Scope/Steps next."
      footer={<>
        <button className="btn ghost" onClick={onClose} disabled={busy}>Cancel</button>
        <button className="btn gold" onClick={submit} disabled={busy} data-testid="sop-create-submit">
          <FileCheck2 size={14} /> {busy ? "Creating…" : "Create & edit"}
        </button>
      </>}>
      <div className="fld" style={{ marginBottom: 12 }}>
        <label>Title</label>
        <input className="input" value={title} onChange={(e) => setTitle(e.target.value)}
          placeholder="e.g. Receiving / New Job" data-testid="sop-new-title" autoFocus />
      </div>
      <div className="fld">
        <label>Category</label>
        <select className="input" value={category} onChange={(e) => setCategory(e.target.value)} data-testid="sop-new-category">
          {SOP_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>
    </Modal>
  );
}
